import { useState, useEffect, useMemo } from 'react'
import { usePlayerStore } from '../../stores'
import { apiClient } from '../../utils/api'
import { verifyUser, managePlayerVisibility, toggleProStatus, overridePlayer, deleteUserAndData } from '../../utils/adminApi'
import { backendClient } from '../../utils/backend'
import AdminUserControl from './AdminUserControl'
import {
  Search, Download, CheckCircle2, X,
  ChevronLeft, ChevronRight, Shield, MoreVertical, Trash2,
  Star, Globe, Lock, Crown, AlertTriangle, Settings
} from 'lucide-react'

type RoleFilter = 'all' | 'player' | 'scout' | 'club' | 'admin'
type StatusFilter = 'all' | 'active' | 'pending' | 'suspended'

interface Profile {
  id: string; email: string; full_name?: string; name?: string; role?: string;
  avatar_url?: string; is_verified?: boolean; is_public?: boolean; created_at?: string;
  state?: string; lga?: string;
}

export default function AdminUserDirectory() {
  const { players } = usePlayerStore()
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [subscriptions, setSubscriptions] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [actionMenuId, setActionMenuId] = useState<string | null>(null)
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; right: number } | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deleteMode, setDeleteMode] = useState<'archive' | 'delete'>('archive')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [viewingUserId, setViewingUserId] = useState<string | null>(null)
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [newUserForm, setNewUserForm] = useState({
    full_name: '',
    email: '',
    org: '',
    role: 'scout' as 'player' | 'scout' | 'club' | 'admin',
    password: 'ScoutaAdmin2026!'
  })
  const perPage = 12

  useEffect(() => {
    async function load() {
      try {
        const [p, subData] = await Promise.all([
          apiClient.getProfiles().catch(() => []),
          backendClient.from('subscriptions').select('user_id, status, current_period_end').then(r => r.data || []),
        ])
        setProfiles(Array.isArray(p) ? p : [])
        const subMap: Record<string, any> = {}
        for (const s of subData) {
          const expired = s.current_period_end && new Date(s.current_period_end) < new Date()
          subMap[s.user_id] = { ...s, isPro: s.status === 'active' && !expired }
        }
        setSubscriptions(subMap)
      } catch { } finally { setLoading(false) }
    }
    load()
  }, [])

  const filtered = useMemo(() => {
    return profiles.filter(p => {
      const q = search.toLowerCase()
      const matchSearch = !q || 
        p.email?.toLowerCase().includes(q) || 
        p.full_name?.toLowerCase().includes(q) || 
        p.name?.toLowerCase().includes(q)
      const matchRole = roleFilter === 'all' || p.role === roleFilter
      const matchStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && p.is_verified) ||
        (statusFilter === 'pending' && !p.is_verified) ||
        (statusFilter === 'suspended' && !p.is_public)
      return matchSearch && matchRole && matchStatus
    })
  }, [profiles, search, roleFilter, statusFilter])

  const paginated = useMemo(() => {
    const start = (page - 1) * perPage
    return filtered.slice(start, start + perPage)
  }, [filtered, page])

  const totalPages = Math.ceil(filtered.length / perPage)

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === paginated.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(paginated.map(p => p.id)))
    }
  }

  const handleVerify = async (id: string, verified: boolean) => {
    setProcessingId(id)
    try {
      const result = await verifyUser(id, verified)
      if (!result.ok) { console.error('Verify failed:', result.error); return }
      setProfiles(prev => prev.map(p => p.id === id ? { ...p, is_verified: verified } : p))
      const store = usePlayerStore.getState()
      const profile = profiles.find(p => p.id === id)
      const updatedPlayers = store.players.map(p => {
        if (p.owner_club_id === id || p.email?.toLowerCase() === profile?.email?.toLowerCase()) {
          return { ...p, is_verified: verified, is_public: verified }
        }
        return p
      })
      usePlayerStore.setState({ players: updatedPlayers })
    } catch (e) { console.error(e) } finally { setProcessingId(null); setActionMenuId(null); setActionMenuPos(null) }
  }

  const handleVisibility = async (id: string, isPublic: boolean) => {
    setProcessingId(id)
    try {
      const profile = profiles.find(pr => pr.id === id)
      // Collect ALL players belonging to this account — by owner_club_id or email/scout match
      const accountPlayerIds = players
        .filter(p =>
          p.owner_club_id === id ||
          (profile?.email && p.email?.toLowerCase() === profile.email.toLowerCase()) ||
          (profile?.email && p.scout?.toLowerCase() === profile.email.toLowerCase())
        )
        .map(p => p.id)
        .filter(Boolean)

      if (accountPlayerIds.length > 0) {
        const result = await managePlayerVisibility(accountPlayerIds, isPublic)
        if (!result.ok) {
          alert('Failed to update visibility: ' + (result.error || 'Unknown error'))
          return
        }
      }
      setProfiles(prev => prev.map(p => p.id === id ? { ...p, is_public: isPublic } : p))
    } catch (e) {
      console.error(e)
      alert('Failed to update player visibility.')
    } finally { setProcessingId(null); setActionMenuId(null); setActionMenuPos(null) }
  }

  const handleTogglePro = async (id: string, active: boolean) => {
    setProcessingId(id)
    try {
      const profile = profiles.find(p => p.id === id)
      const result = await toggleProStatus(id, active, profile?.role || 'player')
      if (!result.ok) {
        alert('Failed to toggle Pro status: ' + (result.error || 'Unknown error'))
        return
      }
      setSubscriptions(prev => ({ ...prev, [id]: { ...prev[id], isPro: active, status: active ? 'active' : 'inactive' } }))
    } catch (e) {
      console.error(e)
      alert('Failed to toggle Pro status.')
    } finally { setProcessingId(null); setActionMenuId(null); setActionMenuPos(null) }
  }

  const handleOverridePlayer = async (playerId: string, field: string, value: any) => {
    setProcessingId(playerId)
    try {
      await overridePlayer(playerId, { [field]: value })
      const store = usePlayerStore.getState()
      const idx = store.players.findIndex(p => p.id === playerId)
      if (idx >= 0) {
        store.players[idx] = { ...store.players[idx], [field]: value }
        usePlayerStore.setState({ players: [...store.players] })
      }
    } catch (e) { console.error(e) } finally { setProcessingId(null) }
  }

  const handleDeleteUser = async (userId: string) => {
    setDeleteLoading(true)
    try {
      const result = await deleteUserAndData(userId, deleteMode)
      if (!result.ok) {
        alert('Failed: ' + (result.error || 'Unknown error'))
        return
      }
      setProfiles(prev => prev.filter(p => p.id !== userId))
      const store = usePlayerStore.getState()
      const profile = profiles.find(p => p.id === userId)
      if (deleteMode === 'delete') {
        usePlayerStore.setState({
          players: store.players.filter(p =>
            p.owner_club_id !== userId &&
            p.scout?.toLowerCase() !== profile?.email?.toLowerCase()
          )
        })
      } else {
        usePlayerStore.setState({
          players: store.players.map(p => {
            if (p.owner_club_id === userId || p.scout?.toLowerCase() === profile?.email?.toLowerCase()) {
              return { ...p, is_visible: false }
            }
            return p
          })
        })
      }
      setDeleteConfirmId(null)
    } catch (e) {
      console.error(e)
      alert('Failed to delete user.')
    } finally {
      setDeleteLoading(false)
      setActionMenuId(null)
      setActionMenuPos(null)
    }
  }

  const handleBulkVerify = async (verified: boolean) => {
    for (const id of selectedIds) {
      const result = await verifyUser(id, verified).catch(() => ({ ok: false }))
      if (result.ok) {
        const profile = profiles.find(p => p.id === id)
        const store = usePlayerStore.getState()
        const updatedPlayers = store.players.map(p => {
          if (p.owner_club_id === id || p.email?.toLowerCase() === profile?.email?.toLowerCase()) {
            return { ...p, is_verified: verified, is_public: verified }
          }
          return p
        })
        usePlayerStore.setState({ players: updatedPlayers })
      }
    }
    setProfiles(prev => prev.map(p => selectedIds.has(p.id) ? { ...p, is_verified: verified } : p))
    setSelectedIds(new Set())
  }

  const exportCSV = () => {
    const headers = ['ID', 'Name', 'Email', 'Role', 'Verified', 'Joined']
    const rows = filtered.map(p => [p.id, p.full_name || p.name || '', p.email, p.role || '', p.is_verified ? 'Yes' : 'No', p.created_at || ''])
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'scouta-users.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const handleCreateUser = async () => {
    const trimmedName = newUserForm.full_name.trim()
    const trimmedEmail = newUserForm.email.trim()
    if (!trimmedName || !trimmedEmail) {
      alert('Please provide a full name and email address.')
      return
    }

    try {
      const { data, error } = await backendClient.auth.signUp({
        email: trimmedEmail,
        password: newUserForm.password || 'ScoutaAdmin2026!',
        options: {
          data: {
            full_name: trimmedName,
            org: newUserForm.org.trim(),
            role: newUserForm.role,
            is_verified: newUserForm.role === 'admin' ? true : false,
          },
        },
      })

      if (error) {
        throw error
      }

      const createdId = data?.user?.id || crypto.randomUUID()
      setProfiles(prev => [{
        id: createdId,
        email: trimmedEmail,
        full_name: trimmedName,
        name: trimmedName,
        role: newUserForm.role,
        org: newUserForm.org.trim(),
        avatar_url: '',
        is_verified: newUserForm.role === 'admin',
        is_public: true,
        created_at: new Date().toISOString(),
      }, ...prev])

      setShowCreateUser(false)
      setNewUserForm({
        full_name: '',
        email: '',
        org: '',
        role: 'scout',
        password: 'ScoutaAdmin2026!'
      })
    } catch (error: any) {
      console.error(error)
      alert(error?.message || 'Failed to create this user account.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#7cff01] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (viewingUserId) {
    return (
      <AdminUserControl
        userId={viewingUserId}
        onBack={() => setViewingUserId(null)}
      />
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white">User Directory</h2>
          <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest">
            Showing {filtered.length.toLocaleString()} users
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={() => handleBulkVerify(true)}
                className="flex items-center gap-2 px-3 py-2 bg-[#7cff01]/10 border border-[#7cff01]/30 rounded-xl text-[#7cff01] text-xs font-bold hover:bg-[#7cff01]/20 transition"
              >
                <CheckCircle2 size={14} />
                Verify ({selectedIds.size})
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition"
              >
                <X size={16} />
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreateUser(true)}
            className="flex items-center gap-2 px-3 py-2 bg-[#7cff01]/10 border border-[#7cff01]/30 rounded-xl text-[#7cff01] text-xs font-bold hover:bg-[#7cff01]/20 transition"
          >
            <Shield size={14} />
            Add User
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-slate-300 text-xs font-bold hover:bg-white/10 transition"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search users, IDs, or clubs..."
            className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/20 transition"
          />
        </div>
        <select
          value={roleFilter}
          onChange={e => { setRoleFilter(e.target.value as RoleFilter); setPage(1) }}
          className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#7cff01]/20 appearance-none cursor-pointer"
        >
          <option value="all">All Roles</option>
          <option value="player">Player</option>
          <option value="scout">Scout</option>
          <option value="club">Club</option>
          <option value="admin">Admin</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value as StatusFilter); setPage(1) }}
          className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#7cff01]/20 appearance-none cursor-pointer"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {showCreateUser && (
        <div className="rounded-2xl border border-[#7cff01]/20 bg-[#101a13] p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-black text-white">Create managed account</h3>
            <button onClick={() => setShowCreateUser(false)} className="text-slate-400 hover:text-white">Close</button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm text-slate-200">
              Full name
              <input
                value={newUserForm.full_name}
                onChange={(e) => setNewUserForm((prev) => ({ ...prev, full_name: e.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0d1117] px-3 py-2 text-white"
              />
            </label>
            <label className="text-sm text-slate-200">
              Role
              <select
                value={newUserForm.role}
                onChange={(e) => setNewUserForm((prev) => ({ ...prev, role: e.target.value as any }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0d1117] px-3 py-2 text-white"
              >
                <option value="scout">Scout</option>
                <option value="club">Club</option>
                <option value="player">Player</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <label className="text-sm text-slate-200 md:col-span-2">
              Email
              <input
                value={newUserForm.email}
                onChange={(e) => setNewUserForm((prev) => ({ ...prev, email: e.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0d1117] px-3 py-2 text-white"
              />
            </label>
            <label className="text-sm text-slate-200 md:col-span-2">
              Organisation / Club
              <input
                value={newUserForm.org}
                onChange={(e) => setNewUserForm((prev) => ({ ...prev, org: e.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0d1117] px-3 py-2 text-white"
                placeholder="Optional organisation or club name"
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => setShowCreateUser(false)}
              className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateUser}
              className="rounded-xl bg-[#7cff01] px-4 py-2 text-sm font-black text-black"
            >
              Create account
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-white/5 bg-[#161b22]">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5 bg-white/[0.02]">
                <th className="p-4 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === paginated.length && paginated.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#7cff01] focus:ring-[#7cff01]/20"
                  />
                </th>
                <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-500">User</th>
                <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hidden md:table-cell">Role</th>
                <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hidden lg:table-cell">Location</th>
                <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hidden sm:table-cell">Joined</th>
                <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-500 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {paginated.map(p => (
                <tr
                  key={p.id}
                  onDoubleClick={() => setViewingUserId(p.id)}
                  className={`cursor-pointer hover:bg-white/[0.02] transition ${selectedIds.has(p.id) ? 'bg-[#7cff01]/[0.03]' : ''}`}
                >
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#7cff01] focus:ring-[#7cff01]/20"
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {p.avatar_url ? (
                          <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs font-bold text-slate-400">{(p.full_name || p.name || '?')[0]}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">{p.full_name || p.name || p.email || 'Unknown'}</p>
                        <p className="text-[10px] text-slate-500 truncate max-w-[180px]">{p.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                      p.role === 'admin' ? 'bg-[#7cff01]/10 text-[#7cff01]' :
                      p.role === 'scout' ? 'bg-blue-500/10 text-blue-400' :
                      p.role === 'club' ? 'bg-purple-500/10 text-purple-400' :
                      'bg-white/5 text-slate-400'
                    }`}>
                      {p.role || 'player'}
                    </span>
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className="text-xs text-slate-400">{p.state || '—'}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        p.is_verified ? 'bg-[#7cff01]' : p.is_public === false ? 'bg-red-400' : 'bg-slate-500'
                      }`} />
                      <span className={`text-[10px] font-black uppercase tracking-wider ${
                        p.is_verified ? 'text-[#7cff01]' : p.is_public === false ? 'text-red-400' : 'text-slate-500'
                      }`}>
                        {p.is_verified ? 'Active' : p.is_public === false ? 'Suspended' : 'Pending'}
                      </span>
                      {subscriptions[p.id]?.isPro && (
                        <span className="ml-1 px-1.5 py-0.5 bg-yellow-500/10 text-yellow-400 text-[8px] font-black uppercase rounded">
                          PRO
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 hidden sm:table-cell">
                    <span className="text-xs text-slate-500">{formatDate(p.created_at)}</span>
                  </td>
                  <td className="p-4 relative">
                    <button
                      onClick={(e) => {
                        if (actionMenuId === p.id) {
                          setActionMenuId(null)
                          setActionMenuPos(null)
                        } else {
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                          const menuH = 420
                          const below = rect.bottom + 4
                          const top = below + menuH > window.innerHeight ? Math.max(8, rect.top - menuH - 4) : below
                          setActionMenuPos({ top, right: Math.max(8, window.innerWidth - rect.right) })
                          setActionMenuId(p.id)
                        }
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition"
                    >
                      <MoreVertical size={14} />
                    </button>
                    {actionMenuId === p.id && actionMenuPos && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => { setActionMenuId(null); setActionMenuPos(null) }} />
                        <div
                          className="fixed z-50 w-56 bg-[#1c2333] border border-white/10 rounded-xl shadow-xl py-1 animate-scale-in"
                          style={{ top: actionMenuPos.top, right: actionMenuPos.right }}
                        >
                          <button
                            onClick={() => { setViewingUserId(p.id); setActionMenuId(null); setActionMenuPos(null) }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white hover:bg-white/5 transition"
                          >
                            <Settings size={14} className="text-[#7cff01]" />
                            Manage Account
                          </button>
                          <div className="h-px bg-white/5 my-1" />
                          <button
                            onClick={() => handleVerify(p.id, !p.is_verified)}
                            disabled={processingId === p.id}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white hover:bg-white/5 transition"
                          >
                            <CheckCircle2 size={14} className={p.is_verified ? 'text-slate-400' : 'text-[#7cff01]'} />
                            {p.is_verified ? 'Unverify Account' : 'Verify Account'}
                          </button>
                          <button
                            onClick={() => handleVisibility(p.id, p.is_public === false)}
                            disabled={processingId === p.id}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white hover:bg-white/5 transition"
                          >
                            {p.is_public === false ? <Globe size={14} /> : <Lock size={14} />}
                            {p.is_public === false ? 'Show on Explore' : 'Hide from Explore'}
                          </button>
                          <div className="border-t border-white/5 my-1" />
                          <button
                            onClick={() => handleTogglePro(p.id, !(subscriptions[p.id]?.isPro))}
                            disabled={processingId === p.id}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/5 transition"
                          >
                            <Crown size={14} className={subscriptions[p.id]?.isPro ? 'text-yellow-400' : 'text-slate-400'} />
                            <span className={subscriptions[p.id]?.isPro ? 'text-yellow-400' : 'text-white'}>
                              {subscriptions[p.id]?.isPro ? 'Deactivate Pro' : 'Activate Pro'}
                            </span>
                          </button>
                          <div className="border-t border-white/5 my-1" />
                          {(() => {
                            const linkedPlayer = players.find(pl =>
                              pl.scout?.toLowerCase() === p.email?.toLowerCase() ||
                              pl.email?.toLowerCase() === p.email?.toLowerCase() ||
                              pl.owner_club_id === p.id
                            )
                            if (!linkedPlayer) return null
                            return (
                              <>
                                <button
                                  onClick={() => handleOverridePlayer(linkedPlayer.id, 'is_verified', !linkedPlayer.is_verified)}
                                  disabled={processingId === p.id}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white hover:bg-white/5 transition"
                                >
                                  <Shield size={14} />
                                  {linkedPlayer.is_verified ? 'Hide Player' : 'Show Player'}
                                </button>
                                <button
                                  onClick={() => handleOverridePlayer(linkedPlayer.id, 'is_top_talent', !linkedPlayer.is_top_talent)}
                                  disabled={processingId === p.id}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white hover:bg-white/5 transition"
                                >
                                  <Star size={14} className={linkedPlayer.is_top_talent ? 'text-yellow-400' : 'text-slate-400'} />
                                  {linkedPlayer.is_top_talent ? 'Remove Top Talent' : 'Mark Top Talent'}
                                </button>
                              </>
                            )
                          })()}
                          <div className="border-t border-white/5 my-1" />
                          <button
                            onClick={() => {
                              setActionMenuId(null)
                              setActionMenuPos(null)
                              setDeleteConfirmId(p.id)
                              setDeleteMode('archive')
                            }}
                            disabled={processingId === p.id}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/5 transition"
                          >
                            <Trash2 size={14} />
                            Suspend Account
                          </button>
                          <button
                            onClick={() => {
                              setActionMenuId(null)
                              setActionMenuPos(null)
                              setDeleteConfirmId(p.id)
                              setDeleteMode('delete')
                            }}
                            disabled={processingId === p.id}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-500 font-bold hover:bg-red-600/10 transition"
                          >
                            <Trash2 size={14} />
                            Delete User & Data
                          </button>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition disabled:opacity-30"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4))
                const p = start + i
                if (p > totalPages) return null
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                      p === page ? 'bg-[#7cff01] text-black' : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {p}
                  </button>
                )
              })}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition disabled:opacity-30"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (() => {
        const target = profiles.find(p => p.id === deleteConfirmId)
        const linkedPlayers = players.filter(p =>
          p.owner_club_id === deleteConfirmId ||
          (target?.email && p.scout?.toLowerCase() === target.email.toLowerCase())
        )
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !deleteLoading && setDeleteConfirmId(null)}>
            <div className="w-full max-w-md bg-[#161b22] border border-white/10 rounded-2xl p-6 space-y-5 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle size={20} className="text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-white">
                    {deleteMode === 'delete' ? 'Delete User' : 'Suspend User'}
                  </h3>
                  <p className="text-xs text-slate-500">{target?.email || deleteConfirmId}</p>
                </div>
              </div>

              {deleteMode === 'delete' ? (
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-2">
                  <p className="text-xs text-red-400 font-bold">
                    This will permanently delete:
                  </p>
                  <ul className="text-xs text-slate-400 space-y-1 ml-3 list-disc">
                    <li>User account and auth credentials</li>
                    <li>Profile and subscription data</li>
                    <li>{linkedPlayers.length} linked player record{linkedPlayers.length !== 1 ? 's' : ''}</li>
                    <li>All conversations and messages</li>
                  </ul>
                  <p className="text-[10px] text-red-500 font-black uppercase tracking-widest mt-2">
                    This action cannot be undone.
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 space-y-2">
                  <p className="text-xs text-yellow-400 font-bold">
                    This will archive the user:
                  </p>
                  <ul className="text-xs text-slate-400 space-y-1 ml-3 list-disc">
                    <li>User account will be hidden from explore</li>
                    <li>{linkedPlayers.length} linked player record{linkedPlayers.length !== 1 ? 's' : ''} will be hidden</li>
                    <li>Data is preserved and can be restored</li>
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  disabled={deleteLoading}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(deleteConfirmId)}
                  disabled={deleteLoading}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition disabled:opacity-50 ${
                    deleteMode === 'delete'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  }`}
                >
                  {deleteLoading ? 'Processing...' : deleteMode === 'delete' ? 'Delete Permanently' : 'Suspend Account'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

function formatDate(date: string | undefined): string {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' })
}
